import { threadWorkspacePanelRoot } from './thread-workspace-panel-root';
import { isUserHomeTrashDirectoryName } from '@/convex/vfs/user_paths';

export type WorkspacePanelDisplayMode = 'panel' | 'expanded';

/**
 * Builds the heading shown above a folder-scoped collection of files.
 * Folder display names are often plural ("Measurements", "Documents"), so
 * singularize only their final word before appending "Files". This keeps the
 * heading derived from folder metadata without maintaining per-folder copy.
 */
export const workspaceFolderFilesLabel = (
  displayName: string | null | undefined
): string => {
  const normalized = displayName?.trim().replace(/\s+/g, ' ');
  if (!normalized) return 'Files';
  if (/\bfiles$/i.test(normalized)) return normalized;

  const words = normalized.split(' ');
  const finalWord = words.at(-1) ?? normalized;
  let singularFinalWord = finalWord;
  if (/[^aeiou]ies$/i.test(finalWord)) {
    singularFinalWord = `${finalWord.slice(0, -3)}y`;
  } else if (/(?:sses|ches|shes|xes|zes)$/i.test(finalWord)) {
    singularFinalWord = finalWord.slice(0, -2);
  } else if (/s$/i.test(finalWord) && !/(?:ss|us|is)$/i.test(finalWord)) {
    singularFinalWord = finalWord.slice(0, -1);
  }
  words[words.length - 1] = singularFinalWord;
  return `${words.join(' ')} Files`;
};

function workspacePathSegments(path: string): string[] {
  return path.trim().replace(/\/+/g, '/').split('/').filter(Boolean);
}

function normalizeAbsoluteWorkspacePanelPath(path: string): string {
  const parts: string[] = [];
  for (const part of path.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') {
      parts.pop();
      continue;
    }
    parts.push(part);
  }
  return `/${parts.join('/')}`;
}

export function isWorkspacePathWithinRoot(
  path: string,
  rootPath: string
): boolean {
  const normalizedPath = normalizeAbsoluteWorkspacePanelPath(path);
  const normalizedRootPath = normalizeAbsoluteWorkspacePanelPath(rootPath);
  return (
    normalizedRootPath === '/' ||
    normalizedPath === normalizedRootPath ||
    normalizedPath.startsWith(`${normalizedRootPath}/`)
  );
}

function absolutizeWorkspacePanelPath(path: string, orgSlug: string): string {
  const trimmed = path.trim();
  if (trimmed.startsWith('/')) {
    return normalizeAbsoluteWorkspacePanelPath(trimmed);
  }
  if (trimmed === orgSlug || trimmed.startsWith(`${orgSlug}/`)) {
    return normalizeAbsoluteWorkspacePanelPath(`/${trimmed}`);
  }
  if (
    trimmed.startsWith('generations/') ||
    trimmed.startsWith('products/') ||
    trimmed.startsWith('projects/') ||
    trimmed === 'References' ||
    trimmed.startsWith('References/') ||
    trimmed === 'shared' ||
    trimmed.startsWith('shared/') ||
    trimmed === 'users' ||
    trimmed.startsWith('users/')
  ) {
    return normalizeAbsoluteWorkspacePanelPath(`/${orgSlug}/${trimmed}`);
  }
  return normalizeAbsoluteWorkspacePanelPath(trimmed);
}

function isFileNativeProductSectionSegment(segment: string): boolean {
  return (
    segment === 'media-studio' ||
    segment === 'catalog-data' ||
    segment === 'prints-and-patterns' ||
    segment === '3d-models'
  );
}

function normalizeCurrentThreadRootPath(
  rootPath: string | null | undefined
): string | null {
  const trimmed = rootPath?.trim();
  if (!trimmed || !trimmed.startsWith('/')) return null;
  return threadWorkspacePanelRoot({
    orgSlug: trimmed.split('/').filter(Boolean)[0] ?? '',
    threadRootPath: trimmed,
  });
}

/** Reads the persisted Home or Drive binding; missing bindings stay unresolved. */
export function threadVfsRootPath(thread: unknown): string | null {
  if (thread === null || typeof thread !== 'object') return null;
  if (!('vfsRootPath' in thread)) return null;
  return normalizeCurrentThreadRootPath(
    typeof thread.vfsRootPath === 'string' ? thread.vfsRootPath : null
  );
}

export function normalizeThreadAssetWorkspacePanelPath(
  path: string,
  args: {
    orgSlug: string;
    currentThreadRef?: string | null;
    /**
     * Exact persisted `orgThreads.vfsRootPath` of the CURRENT thread (e.g. a
     * `/{org}/users/@{workspaceUsername}/generations/thread-assets/{route}`
     * home root; legacy rows may still use an immutable user id segment).
     * Only current-thread placeholders resolve against it; concrete paths —
     * including ones naming other threads — are never rewritten, so a stored
     * root can never be reconstructed from a route segment.
     */
    currentThreadRootPath?: string | null;
  }
): string {
  const normalized = absolutizeWorkspacePanelPath(path, args.orgSlug);
  const segments = workspacePathSegments(normalized);
  if (segments[0] !== args.orgSlug) return '';
  // Panel entity tabs are UI identities, never filesystem locations.
  if (segments[1]?.startsWith('__')) return normalized;
  return (
    threadWorkspacePanelRoot({
      orgSlug: args.orgSlug,
      threadRootPath: normalized,
    }) ?? ''
  );
}

/**
 * True when the exact normalized path sits inside a member's personal home —
 * `/{org}/users/{ownerSegment}` or deeper. Canonical owner segments are
 * readable `@workspaceUsername` values; legacy rows may contain a user id.
 * Uploads landing here must reserve
 * PRIVATE storage (`generatePrivateUploadUrlForMedia`): the backend rejects a
 * public-registry object for a user-home path and never persists a public URL
 * for one. The bare `/{org}/users` root is not a home and returns false.
 */
export function isUserHomeWorkspacePath(
  path: string,
  orgSlug: string
): boolean {
  const segments = workspacePathSegments(
    absolutizeWorkspacePanelPath(path.trim(), orgSlug)
  );
  return (
    segments.length >= 3 && segments[0] === orgSlug && segments[1] === 'users'
  );
}

/**
 * Structural home directories that the file panel may browse but must never
 * offer to rename, move, or delete. Individual thread route folders remain
 * manageable by their owner; only the ownership and collection spine is
 * protected here.
 */
export function isUserHomeStructuralWorkspacePath(
  path: string,
  orgSlug: string
): boolean {
  const segments = workspacePathSegments(
    absolutizeWorkspacePanelPath(path, orgSlug)
  );
  if (segments[0] !== orgSlug || segments[1] !== 'users') return false;
  if (segments.length <= 3) return true;

  const homeSection = segments[3];
  if (segments.length === 4) {
    return (
      isUserHomeTrashDirectoryName(homeSection ?? '') ||
      homeSection === 'generations' ||
      homeSection === 'jobs'
    );
  }
  const collection = segments[4];
  if (segments.length !== 5) return false;
  return (
    (homeSection === 'generations' &&
      (collection === 'thread-assets' || collection === 'user-uploads')) ||
    (homeSection === 'jobs' && collection === 'thread-assets')
  );
}

export function isFixedReferenceWorkspacePath(
  path: string,
  orgSlug: string
): boolean {
  const segments = workspacePathSegments(
    absolutizeWorkspacePanelPath(path, orgSlug)
  );
  if (
    segments[0] !== orgSlug ||
    segments[1] !== 'shared' ||
    segments[2] !== 'References' ||
    segments.length > 4
  ) {
    return false;
  }
  return (
    segments.length === 3 ||
    ['Products', 'Designs', 'Patterns', 'Colors'].includes(segments[3] ?? '')
  );
}

/**
 * True only for a workspace's chat/thread collection root. The private home
 * Chats folder and both legacy/shared thread-assets trees contain route-id
 * folders that the browser can enrich with thread titles.
 */
export function isWorkspaceThreadAssetsRootPath(
  path: string,
  orgSlug: string
): boolean {
  const segments = workspacePathSegments(
    absolutizeWorkspacePanelPath(path, orgSlug)
  );
  return (
    segments.length === 4 &&
    segments[0] === orgSlug &&
    segments[1] === 'users' &&
    segments[3] === 'Chats'
  );
}

export type WorkspacePathBreadcrumbItem = {
  label: string;
  path: string;
  detail?: string;
};

/**
 * Fallback label for the `/{org}/users/{ownerSegment}` ownership boundary when
 * the caller doesn't supply a drive label. New segments are readable
 * `@workspaceUsername` values, while legacy ids must never be exposed as folder
 * labels. Deliberately neutral: a shared thread can be rooted in ANOTHER
 * member's drive, where a first-person label would lie.
 */
export const USER_HOME_BREADCRUMB_LABEL = 'Personal files';

export function workspacePathBreadcrumbItems(
  path: string,
  orgSlug: string,
  options?: {
    /** A fixed label, or one derived from the drive's owner segment. */
    userHomeLabel?: string | ((ownerSegment: string) => string);
    includeReadableUserHomeOwner?: boolean;
    usersRootLabel?: string;
    /**
     * Inside a member's drive, drop the workspace-slug crumb so the drive
     * crumb ("My Drive", "<Name>'s Drive") is the trail's root.
     */
    collapseWorkspaceRootIntoDrive?: boolean;
  }
): WorkspacePathBreadcrumbItem[] {
  const rootPath = `/${orgSlug}`;
  const items: WorkspacePathBreadcrumbItem[] = [
    { label: orgSlug, path: rootPath },
  ];
  const normalizedPath = absolutizeWorkspacePanelPath(path, orgSlug);
  const allSegments = workspacePathSegments(normalizedPath);
  // Never turn a malformed or cross-workspace path into clickable crumbs
  // under the active workspace. The server ACL is authoritative; this guard
  // keeps the picker UI from presenting a misleading navigation target.
  if (allSegments[0] !== orgSlug) return items;
  const segments = allSegments.slice(1);
  if (segments.length === 1 && segments[0] === 'users') {
    items.push({
      label: options?.usersRootLabel ?? 'Users',
      path: `${rootPath}/users`,
    });
    return items;
  }
  let cursor = rootPath;
  let firstPlainSegmentIndex = 0;
  // Collapse `users/{ownerSegment}` into a single crumb. This keeps legacy ids
  // out of the UI and avoids offering navigation to the bare `/users` parent,
  // which would imply browsing other members' homes.
  const userHomeOwnerSegment =
    segments[0] === 'users' ? segments[1] : undefined;
  if (userHomeOwnerSegment) {
    cursor = `${rootPath}/users/${userHomeOwnerSegment}`;
    const userHomeLabel = options?.userHomeLabel;
    if (options?.collapseWorkspaceRootIntoDrive) items.length = 0;
    items.push({
      label:
        typeof userHomeLabel === 'function'
          ? userHomeLabel(userHomeOwnerSegment)
          : (userHomeLabel ?? USER_HOME_BREADCRUMB_LABEL),
      path: cursor,
      ...(options?.includeReadableUserHomeOwner &&
      userHomeOwnerSegment.startsWith('@')
        ? { detail: userHomeOwnerSegment }
        : {}),
    });
    firstPlainSegmentIndex = 2;
  }
  for (const segment of segments.slice(firstPlainSegmentIndex)) {
    cursor = `${cursor}/${segment}`;
    items.push({ label: segment, path: cursor });
  }
  return items;
}

export function isProductWorkspaceFolderPath(path: string): boolean {
  const segments = workspacePathSegments(path);
  const productsIndex = segments.findIndex(
    (segment) => segment.toLowerCase() === 'products'
  );
  if (productsIndex < 0) return false;

  const tail = segments.slice(productsIndex + 1);
  if (tail.length === 1) return true;
  if (tail.length === 2 && isFileNativeProductSectionSegment(tail[1] ?? '')) {
    return true;
  }
  return tail.length === 3 && tail[1] === 'versions';
}

export function shouldOpenWorkspacePathExpanded(path: string): boolean {
  // Editable boards are a primary work surface: open them fullscreen by
  // default regardless of whether the agent remembered --mode fullscreen.
  if (path.toLowerCase().endsWith('.canvas')) return true;
  return isProductWorkspaceFolderPath(path);
}

export function workspacePanelDisplayModeForPath(
  path: string
): WorkspacePanelDisplayMode {
  return shouldOpenWorkspacePathExpanded(path) ? 'expanded' : 'panel';
}
